// Dynamic import of pptxgenjs from CDN to avoid Node.js module issues
declare global {
  interface Window {
    PptxGenJS: any;
  }
}

const loadPptxGenJS = (): Promise<any> => {
  return new Promise((resolve, reject) => {
    if (window.PptxGenJS) {
      resolve(window.PptxGenJS);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/gh/gitbrent/PptxGenJS@3.12.0/dist/pptxgen.bundle.js';
    script.async = true;
    script.onload = () => window.PptxGenJS ? resolve(window.PptxGenJS) : reject(new Error('PptxGenJS failed to load'));
    script.onerror = () => reject(new Error('Failed to load PptxGenJS from CDN'));
    document.head.appendChild(script);
  });
};

export interface StoryTimelineEvent {
  id: string;
  type: 'travel' | 'dwell' | 'observation';
  startTime: Date;
  endTime: Date;
  entityId: string;
  deviceType: string;
  distance?: number;
  pointCount?: number;
  startLocation?: [number, number];
  endLocation?: [number, number];
  tripCount?: number;
  location?: [number, number];
  duration?: number;
  address?: string;
  visitCount?: number;
}

export interface TopLocation {
  location: string;          // Place name or coordinates
  visits: number;
  duration: number;          // Total duration in minutes
  lat?: number;              // For geocoding
  lng?: number;              // For geocoding
  placeName?: string;        // Resolved place name from API
  entities?: string[];       // Which entities visited this location
}

export interface RepeatedVisit {
  location: string;          // Place name
  lat: number;
  lng: number;
  visitCount: number;
  totalDuration: number;     // Total duration in minutes
  firstVisit: string;        // ISO date string
  lastVisit: string;         // ISO date string
  entities: string[];        // Which entities visited
}

export interface CoLocation {
  location: string;          // Place name
  lat: number;
  lng: number;
  entities: string[];        // Entities that co-located
  overlapCount: number;      // Number of times they co-located here
  totalOverlapTime: number;  // Combined overlap time in minutes
}

export interface ChartData {
  dailyRhythm: { hour: string; activity: number }[];
  weeklyActivity: { day: string; activity: number }[];
  topLocations: TopLocation[];
  stats: {
    totalPoints: number;
    uniqueDevices: number;
    totalDwells: number;
    peakHour: number | null;
    avgPointsPerDay: number;
  };
  // New fields for enhanced slides
  repeatedVisits?: RepeatedVisit[];
  coLocations?: CoLocation[];
}

export type PowerPointTheme = 'standard' | 'tactical' | 'executive' | 'minimal' | 'midnight' | 'sandstorm';

export interface ThemeColors {
  titleBg: string;
  contentBg: string;
  primary: string;
  secondary: string;
  text: string;
  lightText: string;
  accent: string;
  titleText: string;
  headerBar: string;
}

export const THEME_DEFINITIONS: Record<PowerPointTheme, { name: string; description: string; colors: ThemeColors }> = {
  standard: {
    name: 'Standard Briefing',
    description: 'Professional navy and white',
    colors: {
      titleBg: '1a2e44',
      contentBg: 'ffffff',
      primary: '1a2e44',
      secondary: '5a6978',
      text: '1f2937',
      lightText: '6b7280',
      accent: '2563eb',
      titleText: 'ffffff',
      headerBar: '1a2e44'
    }
  },
  tactical: {
    name: 'Tactical',
    description: 'Dark slate with OD green accents',
    colors: {
      titleBg: '1c2127',
      contentBg: 'f8f9fa',
      primary: '374151',
      secondary: '6b7280',
      text: '1f2937',
      lightText: '6b7280',
      accent: '4b5e40',
      titleText: 'e5e7eb',
      headerBar: '374151'
    }
  },
  executive: {
    name: 'Executive',
    description: 'Navy with copper accent for leadership',
    colors: {
      titleBg: '0f1a2b',
      contentBg: 'fafbfc',
      primary: '1e3a5f',
      secondary: '64748b',
      text: '1e293b',
      lightText: '64748b',
      accent: 'a0714f',
      titleText: 'f1f5f9',
      headerBar: '1e3a5f'
    }
  },
  minimal: {
    name: 'Minimal',
    description: 'High contrast black and white',
    colors: {
      titleBg: '111827',
      contentBg: 'ffffff',
      primary: '111827',
      secondary: '6b7280',
      text: '111827',
      lightText: '6b7280',
      accent: '374151',
      titleText: 'ffffff',
      headerBar: '111827'
    }
  },
  midnight: {
    name: 'Midnight',
    description: 'Deep indigo with silver accents',
    colors: {
      titleBg: '0f0a1e',
      contentBg: 'f8f7fc',
      primary: '312e81',
      secondary: '6366f1',
      text: '1e1b4b',
      lightText: '6b7280',
      accent: '818cf8',
      titleText: 'e0e7ff',
      headerBar: '1e1b4b'
    }
  },
  sandstorm: {
    name: 'Sandstorm',
    description: 'Desert tan with burnt orange accents',
    colors: {
      titleBg: '292017',
      contentBg: 'faf7f2',
      primary: '78350f',
      secondary: '92400e',
      text: '451a03',
      lightText: '78716c',
      accent: 'd97706',
      titleText: 'fef3c7',
      headerBar: '78350f'
    }
  }
};

export interface PowerPointExportOptions {
  title: string;
  subtitle: string;
  events: StoryTimelineEvent[];
  entities: string[];
  mapScreenshot?: MapScreenshotResult;
  timelineMapScreenshots?: Array<MapScreenshotResult | undefined>;
  chartData?: ChartData;
  aiNarrative?: string;
  eventNotes?: Record<string, { id: number; content: string }[]>;
  includeMap?: boolean;
  includeSummary?: boolean;
  includeTimeline?: boolean;
  includeCharts?: boolean;
  theme?: PowerPointTheme;
}

const SLIDE_W = 13.333;
const SLIDE_H = 7.5;
const TEMPLATE_ASSETS = { logo: '/Circle_Endurion_White.png' } as const;
export const TIMELINE_EVENTS_PER_SLIDE = 22;
const ENTITY_IDS_PER_POWERPOINT_SLIDE = 28;

async function fetchAsDataUrl(path: string): Promise<string> {
  const res = await fetch(path);
  if (!res.ok) throw new Error(`Failed to fetch asset: ${path} (${res.status})`);
  const blob = await res.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error(`Failed to read asset as data URL: ${path}`));
    reader.readAsDataURL(blob);
  });
}

/**
 * Generate smooth gradient background using canvas - no banding artifacts
 */
function generateGradientBackground(width: number, height: number, type: 'title' | 'content', theme: PowerPointTheme): string {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  const colors = THEME_DEFINITIONS[theme].colors;

  if (type === 'title') {
    const baseColor = colors.titleBg;
    const r = parseInt(baseColor.slice(0, 2), 16);
    const g = parseInt(baseColor.slice(2, 4), 16);
    const b = parseInt(baseColor.slice(4, 6), 16);

    ctx.fillStyle = `#${baseColor}`;
    ctx.fillRect(0, 0, width, height);

    // Subtle lighter area in upper center
    const gradient = ctx.createRadialGradient(width * 0.5, height * 0.3, 0, width * 0.5, height * 0.3, width * 0.6);
    gradient.addColorStop(0, `rgba(${Math.min(r + 30, 255)}, ${Math.min(g + 40, 255)}, ${Math.min(b + 50, 255)}, 0.2)`);
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    if (theme === 'executive') {
      const warmGradient = ctx.createRadialGradient(width * 0.5, height * 1.1, 0, width * 0.5, height * 1.1, width * 0.7);
      warmGradient.addColorStop(0, 'rgba(160, 113, 79, 0.1)');
      warmGradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = warmGradient;
      ctx.fillRect(0, 0, width, height);
    }

    if (theme === 'midnight') {
      // Deep purple glow from bottom corner
      const purpleGlow = ctx.createRadialGradient(width * 0.8, height * 0.9, 0, width * 0.8, height * 0.9, width * 0.6);
      purpleGlow.addColorStop(0, 'rgba(99, 102, 241, 0.15)');
      purpleGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = purpleGlow;
      ctx.fillRect(0, 0, width, height);
      // Subtle silver shimmer from top
      const silverShimmer = ctx.createLinearGradient(0, 0, width, height * 0.3);
      silverShimmer.addColorStop(0, 'rgba(224, 231, 255, 0.05)');
      silverShimmer.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = silverShimmer;
      ctx.fillRect(0, 0, width, height * 0.4);
    }

    if (theme === 'sandstorm') {
      // Warm orange glow from lower right - like desert sun
      const sunGlow = ctx.createRadialGradient(width * 0.9, height * 0.7, 0, width * 0.9, height * 0.7, width * 0.5);
      sunGlow.addColorStop(0, 'rgba(217, 119, 6, 0.12)');
      sunGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = sunGlow;
      ctx.fillRect(0, 0, width, height);
      // Dust haze from top
      const dustHaze = ctx.createLinearGradient(0, 0, 0, height * 0.5);
      dustHaze.addColorStop(0, 'rgba(254, 243, 199, 0.08)');
      dustHaze.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = dustHaze;
      ctx.fillRect(0, 0, width, height);
    }
  } else {
    ctx.fillStyle = `#${colors.contentBg}`;
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = `#${colors.headerBar}`;
    ctx.fillRect(0, 0, width, 6);
    ctx.fillStyle = `#${colors.accent}`;
    ctx.globalAlpha = 0.2;
    ctx.fillRect(0, height - 3, width, 3);
    ctx.globalAlpha = 1.0;
  }

  return canvas.toDataURL('image/png');
}

function addFullBleedBackground(slide: any, dataUrl: string) {
  slide.addImage({ data: dataUrl, x: 0, y: 0, w: SLIDE_W, h: SLIDE_H });
}

function addCornerLogo(slide: any, dataUrl: string, opts?: { x?: number; y?: number; w?: number; h?: number }) {
  const { x = SLIDE_W - 1.1, y = 0.15, w = 0.75, h = 0.75 } = opts || {};
  slide.addImage({ data: dataUrl, x, y, w, h });
}

function addFooter(slide: any, footerText: string, textColor = '666666') {
  slide.addText(footerText, { x: 0.5, y: SLIDE_H - 0.35, w: SLIDE_W - 1.0, h: 0.25, fontSize: 9, color: textColor, align: 'right' });
}

function addSlideNumber(slide: any, num: number, total: number, textColor = '999999') {
  slide.addText(`${num} / ${total}`, { x: 0.5, y: SLIDE_H - 0.35, w: 1.0, h: 0.25, fontSize: 9, color: textColor, align: 'left' });
}

function sanitizeAiNarrative(text: string): string {
  return text.replace(/\*\*/g, '').replace(/^\s*#{1,6}\s+/gm, '').replace(/^\s*[-*]\s+/gm, '')
    .split(/\r?\n/).map(l => l.trim()).filter((l, idx) => !(idx === 0 && /^executive summary$/i.test(l)))
    .filter(Boolean).map(l => l.replace(/\s+/g, ' ').trim()).join('\n').trim();
}

function splitTextForSlides(text: string, maxCharsPerSlide: number): string[] {
  const t = text.trim();
  if (!t) return [''];
  const paras = t.split(/\n{2,}/).map(p => p.trim()).filter(Boolean);
  const chunks: string[] = [];
  let cur = '';
  const pushCur = () => { if (cur.trim()) chunks.push(cur.trim()); cur = ''; };

  for (const p of paras) {
    const candidate = cur ? `${cur}\n\n${p}` : p;
    if (candidate.length <= maxCharsPerSlide) { cur = candidate; continue; }
    if (cur) { pushCur(); if (p.length <= maxCharsPerSlide) { cur = p; continue; } }
    const sentences = p.split(/(?<=[.!?])\s+/).map(s => s.trim()).filter(Boolean);
    for (const s of sentences) {
      const cand2 = cur ? `${cur} ${s}` : s;
      if (cand2.length <= maxCharsPerSlide) { cur = cand2; }
      else { if (cur) pushCur(); if (s.length <= maxCharsPerSlide) { cur = s; } else { let i = 0; while (i < s.length) { chunks.push(s.slice(i, i + maxCharsPerSlide).trim()); i += maxCharsPerSlide; } cur = ''; } }
    }
  }
  pushCur();
  return chunks.length ? chunks : [''];
}

function narrativeToBullets(text: string, maxBullets: number): string[] {
  const clean = sanitizeAiNarrative(text);
  if (!clean) return [];
  const parts = clean.split(/\n+/).flatMap(line => line.split(/(?<=[.!?])\s+/)).map(s => s.trim()).filter(s => s.length >= 25);
  const seen = new Set<string>();
  const bullets: string[] = [];
  for (const p of parts) { const key = p.toLowerCase(); if (seen.has(key)) continue; seen.add(key); bullets.push(p); if (bullets.length >= maxBullets) break; }
  return bullets;
}

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m`;
  return `${Math.floor(seconds)}s`;
}

export const exportToPowerPoint = async (options: PowerPointExportOptions): Promise<void> => {
  const {
    title, subtitle, events, entities, mapScreenshot, timelineMapScreenshots, chartData, aiNarrative, eventNotes,
    includeMap = true, includeSummary = true, includeTimeline = true, includeCharts = true, theme = 'standard'
  } = options;

  const entityIdsForAppendix = Array.from(new Set((entities || []).filter(Boolean))).sort();
  const entityAppendixSlideCount =
  includeSummary && events.length > 0 && entityIdsForAppendix.length > 0
    ? Math.ceil(entityIdsForAppendix.length / ENTITY_IDS_PER_POWERPOINT_SLIDE)
    : 0;

  const PptxGenJS = await loadPptxGenJS();
  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_WIDE';
  pptx.author = 'Pathfinder Analysis System';
  pptx.company = 'Quiet Professionals';
  pptx.subject = 'Route Analysis Report';
  pptx.title = title;

  const colors = THEME_DEFINITIONS[theme].colors;
  const titleBgDataUrl = generateGradientBackground(1920, 1080, 'title', theme);
  const contentBgDataUrl = generateGradientBackground(1920, 1080, 'content', theme);

  let logoDataUrl = '';
  try { logoDataUrl = await fetchAsDataUrl(TEMPLATE_ASSETS.logo); } catch (e) { console.warn('Failed to load logo:', e); }

  // Calculate total slides
  let totalSlides = 2;
  if (includeSummary && events.length > 0) {
    totalSlides++;
    totalSlides += entityAppendixSlideCount;
  }
  if (includeMap && mapScreenshot && chartData) totalSlides++; // Quad chart slide
  if (includeMap && mapScreenshot) {
    totalSlides++; // main route visualization slide

    if (aiNarrative) {
      const routeNarrativeChunks = splitTextForSlides(sanitizeAiNarrative(aiNarrative), 1900);
      totalSlides += Math.max(0, routeNarrativeChunks.length - 1); // one continuation per extra chunk
    }
  }
  if (includeCharts && chartData) { 
    totalSlides++; 
    if (chartData.topLocations.length > 0) totalSlides++; 
    // Add slide for repeated visits if they exist (2+ visits)
    if (chartData.repeatedVisits && chartData.repeatedVisits.filter(rv => rv.visitCount >= 2).length > 0) totalSlides++;
    // Add slide for co-locations if they exist
    if (chartData.coLocations && chartData.coLocations.length > 0) totalSlides++;
  }
  if (includeTimeline && events.length > 0) totalSlides += Math.ceil(events.length / TIMELINE_EVENTS_PER_SLIDE);
  if (eventNotes && Object.keys(eventNotes).length > 0) totalSlides++;

  let currentSlide = 0;

  // TITLE SLIDE
  currentSlide++;
  const titleSlide = pptx.addSlide();
  if (titleBgDataUrl) addFullBleedBackground(titleSlide, titleBgDataUrl);
  else titleSlide.background = { color: colors.titleBg };
  if (logoDataUrl) addCornerLogo(titleSlide, logoDataUrl, { x: SLIDE_W - 1.5, y: SLIDE_H - 1.5, w: 1.1, h: 1.1 });

  titleSlide.addText(title, { x: 0.75, y: 2.2, w: SLIDE_W - 1.5, h: 1.0, fontSize: 40, bold: true, color: colors.titleText, align: 'left' });
  titleSlide.addText(subtitle, { x: 0.75, y: 3.3, w: SLIDE_W - 1.5, h: 0.5, fontSize: 22, color: colors.titleText, align: 'left' });

  // OBSERVATIONS SLIDE
  currentSlide++;
  const obsSlide = pptx.addSlide();
  if (contentBgDataUrl) addFullBleedBackground(obsSlide, contentBgDataUrl);
  else obsSlide.background = { color: colors.contentBg };
  addFooter(obsSlide, 'PATHFINDER • UNCLASSIFIED', colors.lightText);
  addSlideNumber(obsSlide, currentSlide, totalSlides, colors.lightText);
  if (logoDataUrl) addCornerLogo(obsSlide, logoDataUrl);

  obsSlide.addText('Observations and Recommendations', { x: 0.5, y: 0.4, w: 12.0, h: 0.6, fontSize: 28, bold: true, color: colors.primary });
  obsSlide.addText('ANALYST COMMENTS', { x: 0.5, y: 1.2, w: 12.3, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
  obsSlide.addText('[Enter analyst observations and assessments here]', { x: 0.5, y: 1.55, w: 12.3, h: 0.8, fontSize: 11, italic: true, color: '888888', line: { color: 'cccccc', width: 1 }, margin: 0.1, valign: 'top' });

  const bullets = aiNarrative ? narrativeToBullets(aiNarrative, 10) : [];
  const keyFindings = bullets.slice(0, 5);
  const actionable = bullets.slice(5, 10);

  obsSlide.addText('AI GENERATED KEY FINDINGS', { x: 0.5, y: 2.6, w: 5.9, h: 0.35, fontSize: 12, bold: true, color: colors.primary });
  obsSlide.addText((keyFindings.length ? keyFindings : ['[Add key findings from analysis]']).join('\n'), { x: 0.5, y: 3.0, w: 5.9, h: 4.0, fontSize: 10, color: colors.text, valign: 'top', bullet: { indent: 14 }, lineSpacing: 13, margin: 0.08 });
  obsSlide.addText('AI GENERATED ACTIONABLE CONCLUSIONS', { x: 6.8, y: 2.6, w: 5.9, h: 0.35, fontSize: 12, bold: true, color: colors.primary });
  obsSlide.addText((actionable.length ? actionable : ['[Add recommended actions]']).join('\n'), { x: 6.8, y: 3.0, w: 5.9, h: 4.0, fontSize: 10, color: colors.text, valign: 'top', bullet: { indent: 14 }, lineSpacing: 13, margin: 0.08 });

  // EXECUTIVE SUMMARY
  if (includeSummary && events.length > 0) {
    currentSlide++;
    const summarySlide = pptx.addSlide();
    if (contentBgDataUrl) addFullBleedBackground(summarySlide, contentBgDataUrl);
    else summarySlide.background = { color: colors.contentBg };
    addFooter(summarySlide, 'PATHFINDER • UNCLASSIFIED', colors.lightText);
    addSlideNumber(summarySlide, currentSlide, totalSlides, colors.lightText);
    if (logoDataUrl) addCornerLogo(summarySlide, logoDataUrl);

    summarySlide.addText('Executive Summary', { x: 0.5, y: 0.4, w: 12.0, h: 0.6, fontSize: 28, bold: true, color: colors.primary });

    const travelEvents = events.filter(e => e.type === 'travel');
    const dwellEvents = events.filter(e => e.type === 'dwell');
    const totalTravelDistance = travelEvents.reduce((sum, e) => sum + (e.distance || 0), 0);
    const totalTravelTime = travelEvents.reduce((sum, e) => sum + (e.endTime.getTime() - e.startTime.getTime()) / 1000, 0);
    const totalDwellTime = dwellEvents.reduce((sum, e) => sum + (e.endTime.getTime() - e.startTime.getTime()) / 1000, 0);

    const stats = [
      { label: 'Total Events', value: events.length.toString() },
      { label: 'Travel Segments', value: travelEvents.length.toString() },
      { label: 'Dwell Locations', value: dwellEvents.length.toString() },
      { label: 'Total Distance', value: `${(totalTravelDistance * 0.621371).toFixed(2)} mi` },
      { label: 'Travel Time', value: formatDuration(totalTravelTime) },
      { label: 'Dwell Time', value: formatDuration(totalDwellTime) }
    ];

    const summaryData = stats.map((stat, idx) => [
      { text: stat.label, options: { bold: true, color: colors.text, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } },
      { text: stat.value, options: { color: colors.secondary, align: 'right' as const, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } }
    ]);

    summarySlide.addTable(summaryData, { x: 0.5, y: 1.3, w: 5.5, rowH: 0.45, fontSize: 14, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [3.5, 2.0] });

    const firstEvent = events[0];
    const lastEvent = events[events.length - 1];
    summarySlide.addText('ANALYSIS PERIOD', { x: 6.5, y: 1.3, w: 6.0, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
    summarySlide.addText(`${firstEvent.startTime.toLocaleDateString()} – ${lastEvent.endTime.toLocaleDateString()}`, { x: 6.5, y: 1.65, w: 6.0, h: 0.3, fontSize: 14, color: colors.text });
    summarySlide.addText('ENTITIES ANALYZED', { x: 6.5, y: 2.3, w: 6.0, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
    const n = entityIdsForAppendix.length;
    const appendixNote =
      n === 0
        ? 'None'
        : entityAppendixSlideCount > 1
          ? `${n} entities — full list on the following ${entityAppendixSlideCount} slides`
          : `${n} entit${n === 1 ? 'y' : 'ies'} — full list on the following slide`;
    summarySlide.addText(appendixNote, { x: 6.5, y: 2.65, w: 6.0, h: 0.9, fontSize: 12, color: colors.text, valign: 'top' });
  }

  // ENTITIES ANALYZED — appendix slides (full ID list, paginated)
  if (includeSummary && events.length > 0 && entityAppendixSlideCount > 0) {
    for (let p = 0; p < entityAppendixSlideCount; p++) {
      currentSlide++;
      const entitySlide = pptx.addSlide();
      if (contentBgDataUrl) addFullBleedBackground(entitySlide, contentBgDataUrl);
      else entitySlide.background = { color: colors.contentBg };
      addFooter(entitySlide, 'PATHFINDER • UNCLASSIFIED', colors.lightText);
      addSlideNumber(entitySlide, currentSlide, totalSlides, colors.lightText);
      if (logoDataUrl) addCornerLogo(entitySlide, logoDataUrl);

      const pageSuffix = entityAppendixSlideCount > 1 ? ` (${p + 1} of ${entityAppendixSlideCount})` : '';
      entitySlide.addText(`Entities Analyzed${pageSuffix}`, {
        x: 0.5, y: 0.4, w: 12.0, h: 0.55,
        fontSize: 28, bold: true, color: colors.primary,
      });

      entitySlide.addText(`${entityIdsForAppendix.length} total entities`, {
        x: 0.5, y: 0.92, w: 12.0, h: 0.25,
        fontSize: 11, italic: true, color: colors.lightText,
      });

      const start = p * ENTITY_IDS_PER_POWERPOINT_SLIDE;
      const pageIds = entityIdsForAppendix.slice(start, start + ENTITY_IDS_PER_POWERPOINT_SLIDE);

      const half = Math.ceil(pageIds.length / 2);
      const leftCol = pageIds.slice(0, half);
      const rightCol = pageIds.slice(half);
      while (rightCol.length < leftCol.length) rightCol.push('');

      const indexOffset = p * ENTITY_IDS_PER_POWERPOINT_SLIDE;

            const headerFill = { color: 'e5e7eb' };
      const entityTableHeader = [
        { text: '#', options: { bold: true, color: colors.primary, fill: headerFill, align: 'right' as const, fontSize: 9 } },
        { text: 'Entity ID', options: { bold: true, color: colors.primary, fill: headerFill, fontSize: 9 } },
        { text: '#', options: { bold: true, color: colors.primary, fill: headerFill, align: 'right' as const, fontSize: 9 } },
        { text: 'Entity ID', options: { bold: true, color: colors.primary, fill: headerFill, fontSize: 9 } },
      ];

      const entityTableData = [
        entityTableHeader,
        ...leftCol.map((entity, idx) => {
          const leftIndex = indexOffset + idx + 1;
          const rightIndex = indexOffset + half + idx + 1;
          const rightEntity = rightCol[idx];
          const rowFill = { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' };

          return [
            {
              text: `${leftIndex}`,
              options: { color: colors.lightText, fill: rowFill, align: 'right' as const },
            },
            {
              text: entity,
              options: { color: colors.text, fill: rowFill },
            },
            {
              text: rightEntity ? `${rightIndex}` : '',
              options: { color: colors.lightText, fill: rowFill, align: 'right' as const },
            },
            {
              text: rightEntity,
              options: { color: colors.text, fill: rowFill },
            },
          ];
        }),
      ];

      entitySlide.addTable(entityTableData, {
        x: 0.4, y: 1.25,
        w: 12.2,
        rowH: 0.22,
        fontSize: 8.5,
        border: { type: 'solid', pt: 0.5, color: 'e5e7eb' },
        colW: [0.4, 5.6, 0.4, 5.8],
      });
    }
  }

  // QUAD CHART OVERVIEW SLIDE - Mission Overview with map and key data
  if (includeMap && mapScreenshot && chartData) {
    currentSlide++;
    const quadSlide = pptx.addSlide();
    if (contentBgDataUrl) addFullBleedBackground(quadSlide, contentBgDataUrl);
    else quadSlide.background = { color: colors.contentBg };
    addFooter(quadSlide, 'PATHFINDER • UNCLASSIFIED', colors.lightText);
    addSlideNumber(quadSlide, currentSlide, totalSlides, colors.lightText);
    if (logoDataUrl) addCornerLogo(quadSlide, logoDataUrl);

    // Build date range string
    let dateRangeStr = '';
    if (events.length > 0) {
      const firstEvent = events[0];
      const lastEvent = events[events.length - 1];
      dateRangeStr = ` (${firstEvent.startTime.toLocaleDateString()} – ${lastEvent.endTime.toLocaleDateString()})`;
    }

    quadSlide.addText(`Mission Overview ${dateRangeStr}`, { x: 0.5, y: 0.3, w: 11.5, h: 0.45, fontSize: 20, bold: true, color: colors.primary });

    // QUAD LAYOUT - 2x2 grid
    const quadW = 6.1;  // Width of each quadrant
    const quadH = 3.0;  // Height of each quadrant
    const leftX = 0.4;
    const rightX = 6.7;
    const topY = 0.85;
    const bottomY = 3.95;

    // TOP LEFT - Route Map
    quadSlide.addText('ROUTE MAP', { x: leftX, y: topY, w: quadW, h: 0.25, fontSize: 9, bold: true, color: colors.primary });
    
    const mapMaxW = quadW - 0.1;
    const mapMaxH = quadH - 0.35;
    const mapAr = mapScreenshot.aspectRatio;
    let mapImgW = mapMaxW, mapImgH = mapMaxW / mapAr;
    if (mapImgH > mapMaxH) { mapImgH = mapMaxH; mapImgW = mapMaxH * mapAr; }
    const mapImgX = leftX + (mapMaxW - mapImgW) / 2;
    const mapImgY = topY + 0.28 + (mapMaxH - mapImgH) / 2;
    
    quadSlide.addImage({ data: mapScreenshot.dataUrl, x: mapImgX, y: mapImgY, w: mapImgW, h: mapImgH });

    // TOP RIGHT - Key Statistics
    quadSlide.addText('KEY STATISTICS', { x: rightX, y: topY, w: quadW, h: 0.25, fontSize: 9, bold: true, color: colors.primary });
    
    const travelEvents = events.filter(e => e.type === 'travel');
    const dwellEvents = events.filter(e => e.type === 'dwell');
    const totalDistance = travelEvents.reduce((sum, e) => sum + (e.distance || 0), 0);
    const totalTravelTime = travelEvents.reduce((sum, e) => sum + (e.endTime.getTime() - e.startTime.getTime()) / 1000, 0);
    const totalDwellTime = dwellEvents.reduce((sum, e) => sum + (e.endTime.getTime() - e.startTime.getTime()) / 1000, 0);
    
    const quadStats = [
      ['Total GPS Points', chartData.stats.totalPoints.toLocaleString()],
      ['Unique Devices', chartData.stats.uniqueDevices.toString()],
      ['Travel Segments', travelEvents.length.toString()],
      ['Dwell Locations', dwellEvents.length.toString()],
      ['Total Distance', `${(totalDistance * 0.621371).toFixed(1)} mi`],
      ['Travel Time', formatDuration(totalTravelTime)],
      ['Dwell Time', formatDuration(totalDwellTime)],
      ['Peak Hour', chartData.stats.peakHour !== null ? `${chartData.stats.peakHour.toString().padStart(2, '0')}:00` : 'N/A'],
    ].map(([label, value], idx) => [
      { text: label, options: { bold: true, fontSize: 9, color: colors.text, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } },
      { text: value, options: { fontSize: 9, color: colors.secondary, align: 'right' as const, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } }
    ]);
    
    quadSlide.addTable(quadStats, { x: rightX, y: topY + 0.28, w: quadW, rowH: 0.32, fontSize: 9, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [3.8, 2.3] });

    // BOTTOM LEFT - Activity Heatmap (Weekly + Peak Hours)
    quadSlide.addText('ACTIVITY PATTERNS', { x: leftX, y: bottomY, w: quadW, h: 0.25, fontSize: 9, bold: true, color: colors.primary });
    
    if (chartData.weeklyActivity.length > 0) {
      const maxWeekly = Math.max(...chartData.weeklyActivity.map(d => d.activity)) || 1;
      const weeklyRows = chartData.weeklyActivity.map((d, idx) => {
        const barWidth = Math.round((d.activity / maxWeekly) * 12);
        const bar = '▓'.repeat(barWidth) + '░'.repeat(12 - barWidth);
        return [
          { text: d.day.slice(0, 3), options: { bold: true, fontSize: 8, color: colors.text, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } },
          { text: bar, options: { fontSize: 7, color: colors.accent, fontFace: 'Consolas', fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } },
          { text: d.activity.toString(), options: { fontSize: 8, color: colors.secondary, align: 'right' as const, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } }
        ];
      });
      quadSlide.addTable(weeklyRows, { x: leftX, y: bottomY + 0.28, w: 3.8, rowH: 0.27, fontSize: 8, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [0.55, 2.45, 0.8] });
      
      // Add mini hourly heatmap next to weekly
      if (chartData.dailyRhythm.length > 0) {
        quadSlide.addText('Peak Hours:', { x: leftX + 4.0, y: bottomY + 0.28, w: 2.0, h: 0.2, fontSize: 8, bold: true, color: colors.text });
        
        // Find top 5 peak hours
        const sortedHours = [...chartData.dailyRhythm].sort((a, b) => b.activity - a.activity).slice(0, 5);
        const peakHoursText = sortedHours.filter(h => h.activity > 0).map(h => `${h.hour}: ${h.activity}`).join('\n');
        quadSlide.addText(peakHoursText || 'No activity', { x: leftX + 4.0, y: bottomY + 0.5, w: 2.0, h: 2.2, fontSize: 8, color: colors.text, valign: 'top', lineSpacing: 11 });
      }
    } else {
      quadSlide.addText('No activity data available', { x: leftX, y: bottomY + 0.5, w: quadW, h: 0.3, fontSize: 10, color: colors.lightText, italic: true });
    }

    // BOTTOM RIGHT - Key Findings / Top Locations
    quadSlide.addText('KEY FINDINGS', { x: rightX, y: bottomY, w: quadW, h: 0.25, fontSize: 9, bold: true, color: colors.primary });
    
    const findingsBullets: string[] = [];
    
    // Add top locations insight
    if (chartData.topLocations.length > 0) {
      const topLoc = chartData.topLocations[0];
      const locName = topLoc.placeName || (topLoc.lat && topLoc.lng ? `${topLoc.lat.toFixed(3)}, ${topLoc.lng.toFixed(3)}` : topLoc.location);
      findingsBullets.push(`Most visited: ${locName} (${topLoc.visits} visits)`);
    }
    
    // Add repeated visits insight
    if (chartData.repeatedVisits && chartData.repeatedVisits.length > 0) {
      const repeated = chartData.repeatedVisits.filter(rv => rv.visitCount >= 2);
      if (repeated.length > 0) {
        findingsBullets.push(`${repeated.length} locations visited multiple times`);
      }
    }
    
    // Add co-location insight
    if (chartData.coLocations && chartData.coLocations.length > 0) {
      findingsBullets.push(`${chartData.coLocations.length} shared locations between entities`);
    }
    
    // Add travel pattern insight
    if (travelEvents.length > 0) {
      const avgTripDist = (totalDistance / travelEvents.length) * 0.621371;
      findingsBullets.push(`Avg trip distance: ${avgTripDist.toFixed(1)} miles`);
    }
    
    // Add dwell pattern insight
    if (dwellEvents.length > 0) {
      const avgDwellMins = (totalDwellTime / dwellEvents.length) / 60;
      findingsBullets.push(`Avg dwell time: ${avgDwellMins.toFixed(0)} minutes`);
    }
    
    // Add AI-generated findings if available
    if (aiNarrative) {
      const aiBullets = narrativeToBullets(aiNarrative, 3);
      findingsBullets.push(...aiBullets.slice(0, 3 - findingsBullets.length > 0 ? 3 - findingsBullets.length : 0));
    }
    
    // Ensure we have at least some content
    if (findingsBullets.length === 0) {
      findingsBullets.push('Analysis complete - review detailed slides for insights');
    }
    
    quadSlide.addText(findingsBullets.slice(0, 8).join('\n'), { 
      x: rightX, y: bottomY + 0.28, w: quadW, h: quadH - 0.35, 
      fontSize: 9, color: colors.text, valign: 'top', 
      bullet: { indent: 10 }, lineSpacing: 12, margin: 0.05 
    });
  }

  // ROUTE VISUALIZATION SLIDE
  if (includeMap && mapScreenshot) {
    currentSlide++;
    const mapSlide = pptx.addSlide();
    if (contentBgDataUrl) addFullBleedBackground(mapSlide, contentBgDataUrl);
    else mapSlide.background = { color: colors.contentBg };
    addFooter(mapSlide, 'PATHFINDER • UNCLASSIFIED', colors.lightText);
    addSlideNumber(mapSlide, currentSlide, totalSlides, colors.lightText);
    if (logoDataUrl) addCornerLogo(mapSlide, logoDataUrl);

    let timelineStr = '';
    if (events.length > 0) {
      const firstEvent = events[0];
      const lastEvent = events[events.length - 1];
      timelineStr = ` (${firstEvent.startTime.toLocaleDateString()} – ${lastEvent.endTime.toLocaleDateString()})`;
    }

    mapSlide.addText(`Route Visualization ${timelineStr}`, { x: 0.5, y: 0.35, w: 11.5, h: 0.5, fontSize: 22, bold: true, color: colors.primary });

    const mapMaxWidth = 5.8, mapMaxHeight = 5.5;
    const aspectRatio = mapScreenshot.aspectRatio;
    let imgWidth: number, imgHeight: number;
    if (aspectRatio > (mapMaxWidth / mapMaxHeight)) { imgWidth = mapMaxWidth; imgHeight = mapMaxWidth / aspectRatio; }
    else { imgHeight = mapMaxHeight; imgWidth = mapMaxHeight * aspectRatio; }
    const mapX = 0.5, mapY = 1.0 + (mapMaxHeight - imgHeight) / 2;

    mapSlide.addImage({ data: mapScreenshot.dataUrl, x: mapX, y: mapY, w: imgWidth, h: imgHeight });

    const rightX = 6.6, rightW = 6.2;

    if (aiNarrative) {
      mapSlide.addText('ANALYST COMMENTS', { x: rightX, y: 1.0, w: rightW, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
      mapSlide.addText('[Enter comments]', { x: rightX, y: 1.35, w: rightW, h: 0.6, fontSize: 10, color: '888888', italic: true, line: { color: 'cccccc', width: 1 }, margin: 0.08, valign: 'top' });
      mapSlide.addText('AI-GENERATED ANALYSIS', { x: rightX, y: 2.15, w: rightW, h: 0.3, fontSize: 11, bold: true, color: colors.primary });

      const cleanNarrative = sanitizeAiNarrative(aiNarrative);
      const aiChunks = splitTextForSlides(cleanNarrative, 1900);
      mapSlide.addText(aiChunks[0] || '', { x: rightX, y: 2.5, w: rightW, h: 4.0, fontSize: 9, color: colors.text, align: 'left', valign: 'top', lineSpacing: 12, margin: 0.08 });

      // Combine remaining AI chunks into a single continuation slide with title and map
      for (let i = 1; i < aiChunks.length; i++) {
        currentSlide++;
        const cont = pptx.addSlide();
        if (contentBgDataUrl) addFullBleedBackground(cont, contentBgDataUrl);
        else cont.background = { color: colors.contentBg };
        addFooter(cont, 'PATHFINDER • UNCLASSIFIED', colors.lightText);
        addSlideNumber(cont, currentSlide, totalSlides, colors.lightText);
        if (logoDataUrl) addCornerLogo(cont, logoDataUrl);

        // Add same title as the main route visualization slide
        let contTimelineStr = '';
        if (events.length > 0) {
          const firstEvent = events[0];
          const lastEvent = events[events.length - 1];
          contTimelineStr = ` (${firstEvent.startTime.toLocaleDateString()} – ${lastEvent.endTime.toLocaleDateString()})`;
        }
        cont.addText(`Route Visualization: ${contTimelineStr}`, { x: 0.5, y: 0.35, w: 11.5, h: 0.5, fontSize: 22, bold: true, color: colors.primary });

        // Add map on the left side (same as main route visualization slide)
        const contMapMaxWidth = 5.8, contMapMaxHeight = 5.5;
        const contAspectRatio = mapScreenshot.aspectRatio;
        let contImgWidth: number, contImgHeight: number;
        if (contAspectRatio > (contMapMaxWidth / contMapMaxHeight)) { contImgWidth = contMapMaxWidth; contImgHeight = contMapMaxWidth / contAspectRatio; }
        else { contImgHeight = contMapMaxHeight; contImgWidth = contMapMaxHeight * contAspectRatio; }
        const contMapX = 0.5, contMapY = 1.0 + (contMapMaxHeight - contImgHeight) / 2;
        cont.addImage({ data: mapScreenshot.dataUrl, x: contMapX, y: contMapY, w: contImgWidth, h: contImgHeight });

        // Combine all remaining chunks and display on the right side
        const contRightX = 6.6, contRightW = 6.2;
        cont.addText('AI-GENERATED ANALYSIS (CONTINUED)', { x: contRightX, y: 1.0, w: contRightW, h: 0.3, fontSize: 11, bold: true, color: colors.primary 
        });
        cont.addText(aiChunks[i] || '', { x: contRightX, y: 1.35, w: contRightW, h: 5.15, fontSize: 9, color: colors.text, align: 'left', valign: 'top', lineSpacing: 12, margin: 0.08
        });
      }
    } else {
      const travelEvents = events.filter(e => e.type === 'travel');
      const dwellEvents = events.filter(e => e.type === 'dwell');
      const totalDistance = travelEvents.reduce((sum, e) => sum + (e.distance || 0), 0);
      mapSlide.addText('SUMMARY STATISTICS', { x: rightX, y: 1.0, w: rightW, h: 0.35, fontSize: 12, bold: true, color: colors.primary });
      mapSlide.addText(`Total Events: ${events.length}\nTravel Segments: ${travelEvents.length}\nDwell Locations: ${dwellEvents.length}\nTotal Distance: ${(totalDistance * 0.621371).toFixed(2)} miles`, { x: rightX, y: 1.5, w: rightW, h: 3.0, fontSize: 12, color: colors.text, lineSpacing: 20 });
    }
  }

  // ACTIVITY PATTERNS
  if (includeCharts && chartData) {
    currentSlide++;
    const activitySlide = pptx.addSlide();
    if (contentBgDataUrl) addFullBleedBackground(activitySlide, contentBgDataUrl);
    else activitySlide.background = { color: colors.contentBg };
    addFooter(activitySlide, 'PATHFINDER • UNCLASSIFIED', colors.lightText);
    addSlideNumber(activitySlide, currentSlide, totalSlides, colors.lightText);
    if (logoDataUrl) addCornerLogo(activitySlide, logoDataUrl);

    activitySlide.addText('Activity Patterns', { x: 0.5, y: 0.4, w: 12.0, h: 0.5, fontSize: 28, bold: true, color: colors.primary });

    // Generate text summary of the activity patterns (placed at top of slide)
    const summaryParts: string[] = [];
    
    // Intro
    summaryParts.push(`This slide summarizes activity patterns based on ${chartData.stats.totalPoints.toLocaleString()} GPS data points from ${chartData.stats.uniqueDevices} device(s).`);
    
    // Peak hour insight
    if (chartData.stats.peakHour !== null) {
      const peakHourFormatted = `${chartData.stats.peakHour.toString().padStart(2, '0')}:00`;
      const peakHourEnd = `${((chartData.stats.peakHour + 1) % 24).toString().padStart(2, '0')}:00`;
      summaryParts.push(`Peak activity occurs between ${peakHourFormatted}–${peakHourEnd}.`);
    }
    
    // Weekly pattern insight
    if (chartData.weeklyActivity.length > 0) {
      const maxWeeklyActivity = Math.max(...chartData.weeklyActivity.map(d => d.activity));
      const peakDays = chartData.weeklyActivity.filter(d => d.activity === maxWeeklyActivity).map(d => d.day);
      const activeDays = chartData.weeklyActivity.filter(d => d.activity > 0).map(d => d.day);
      if (peakDays.length > 0 && maxWeeklyActivity > 0) {
        summaryParts.push(`Most active day(s): ${peakDays.join(', ')}.`);
      }
      if (activeDays.length < 7 && activeDays.length > 0) {
        summaryParts.push(`Activity recorded on ${activeDays.length} of 7 days.`);
      }
    }
    
    // Dwell insight
    if (chartData.stats.totalDwells > 0) {
      summaryParts.push(`${chartData.stats.totalDwells} dwell location(s) identified during the analysis period.`);
    }
    
    // Daily average
    if (chartData.stats.avgPointsPerDay > 0) {
      summaryParts.push(`Average of ${chartData.stats.avgPointsPerDay.toLocaleString()} data points collected per day.`);
    }
    
    const summaryText = summaryParts.join(' ');
    
    activitySlide.addText(summaryText, { x: 0.5, y: 0.95, w: 12.3, h: 0.9, fontSize: 11, color: colors.text, valign: 'top', lineSpacing: 14 });

    const statsData = [
      ['Total GPS Points', chartData.stats.totalPoints.toLocaleString()],
      ['Unique Devices', chartData.stats.uniqueDevices.toString()],
      ['Total Dwells', chartData.stats.totalDwells.toString()],
      ['Peak Activity Hour', chartData.stats.peakHour !== null ? `${chartData.stats.peakHour.toString().padStart(2, '0')}:00` : 'N/A'],
      ['Avg Points/Day', chartData.stats.avgPointsPerDay.toString()]
    ].map(([label, value], idx) => [
      { text: label, options: { bold: true, color: colors.text, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } },
      { text: value, options: { color: colors.secondary, align: 'right' as const, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } }
    ]);

    activitySlide.addText('STATISTICS', { x: 0.5, y: 2.0, w: 4.5, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
    activitySlide.addTable(statsData, { x: 0.5, y: 2.35, w: 4.5, rowH: 0.35, fontSize: 11, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [2.8, 1.7] });

    if (chartData.weeklyActivity.length > 0) {
      activitySlide.addText('WEEKLY ACTIVITY', { x: 5.5, y: 2.0, w: 4.5, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
      const maxWeekly = Math.max(...chartData.weeklyActivity.map(d => d.activity)) || 1;
      const weeklyRows = chartData.weeklyActivity.map((d, idx) => {
        const barWidth = Math.round((d.activity / maxWeekly) * 15);
        const bar = '▓'.repeat(barWidth) + '░'.repeat(15 - barWidth);
        return [
          { text: d.day.slice(0, 3), options: { bold: true, color: colors.text, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } },
          { text: bar, options: { color: colors.accent, fontFace: 'Consolas', fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } },
          { text: d.activity.toString(), options: { color: colors.secondary, align: 'right' as const, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } }
        ];
      });
      activitySlide.addTable(weeklyRows, { x: 5.5, y: 2.35, w: 4.5, rowH: 0.3, fontSize: 9, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [0.6, 2.9, 1.0] });
    }

    if (chartData.dailyRhythm.length > 0) {
      activitySlide.addText('HOURLY ACTIVITY DISTRIBUTION', { x: 0.5, y: 6.15, w: 9.0, h: 0.25, fontSize: 10, bold: true, color: colors.primary });
      
      const maxHourly = Math.max(...chartData.dailyRhythm.map(d => d.activity)) || 1;
      
      // Create a horizontal heatmap bar across the bottom
      // Color intensity based on activity level, using theme accent color
      const accentR = parseInt(colors.accent.slice(0, 2), 16);
      const accentG = parseInt(colors.accent.slice(2, 4), 16);
      const accentB = parseInt(colors.accent.slice(4, 6), 16);
      
      const getHeatmapColor = (activity: number): string => {
        if (activity === 0) return 'f3f4f6'; // Light gray for zero
        const intensity = activity / maxHourly;
        // Scale from light tint of accent to full accent color
        // Start: light version (blend with white), End: full accent
        const lightR = 240, lightG = 240, lightB = 240; // Very light gray as base
        const r = Math.round(lightR + (accentR - lightR) * intensity);
        const g = Math.round(lightG + (accentG - lightG) * intensity);
        const b = Math.round(lightB + (accentB - lightB) * intensity);
        return r.toString(16).padStart(2, '0') + g.toString(16).padStart(2, '0') + b.toString(16).padStart(2, '0');
      };
      
      // Determine if text should be light or dark based on background intensity
      const getTextColor = (activity: number): string => {
        if (activity === 0) return colors.lightText;
        const intensity = activity / maxHourly;
        // Calculate luminance of the blended color to determine text contrast
        const r = 240 + (accentR - 240) * intensity;
        const g = 240 + (accentG - 240) * intensity;
        const b = 240 + (accentB - 240) * intensity;
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance < 0.5 ? 'ffffff' : colors.text;
      };
      
      // Build hour labels row and values row for horizontal heatmap
      const hourLabels = chartData.dailyRhythm.map((d) => ({
        text: d.hour.replace(':00', '').padStart(2, '0'),
        options: { 
          fontSize: 7, 
          color: getTextColor(d.activity),
          fill: { color: getHeatmapColor(d.activity) },
          align: 'center' as const,
          valign: 'middle' as const
        }
      }));
      
      const activityValues = chartData.dailyRhythm.map((d) => ({
        text: d.activity > 0 ? d.activity.toString() : '',
        options: { 
          fontSize: 6, 
          color: getTextColor(d.activity),
          fill: { color: getHeatmapColor(d.activity) },
          align: 'center' as const,
          valign: 'middle' as const
        }
      }));
      
      // Create the horizontal heatmap as a 2-row table spanning full width
      const heatmapX = 0.5;
      const heatmapW = 12.3;
      const cellW = heatmapW / 24;
      
      activitySlide.addTable(
        [[...hourLabels], [...activityValues]],
        { 
          x: heatmapX, 
          y: 6.45, 
          w: heatmapW, 
          rowH: 0.25, 
          fontSize: 7, 
          border: { type: 'solid', pt: 0.5, color: 'ffffff' },
          colW: Array(24).fill(cellW)
        }
      );
    }

    // TOP LOCATIONS
    if (chartData.topLocations.length > 0) {
      currentSlide++;
      const locationsSlide = pptx.addSlide();
      if (contentBgDataUrl) addFullBleedBackground(locationsSlide, contentBgDataUrl);
      else locationsSlide.background = { color: colors.contentBg };
      addFooter(locationsSlide, 'PATHFINDER • UNCLASSIFIED', colors.lightText);
      addSlideNumber(locationsSlide, currentSlide, totalSlides, colors.lightText);
      if (logoDataUrl) addCornerLogo(locationsSlide, logoDataUrl);

      locationsSlide.addText('Top Dwell Locations', { x: 0.5, y: 0.4, w: 12.0, h: 0.5, fontSize: 28, bold: true, color: colors.primary });
      locationsSlide.addText(`Ranked by total dwell time (top ${Math.min(chartData.topLocations.length, 12)})`, { x: 0.5, y: 0.9, w: 12.0, h: 0.25, fontSize: 11, color: colors.lightText });

      const locTableData: any[] = [[
        { text: '#', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
        { text: 'Location', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
        { text: 'Visits', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
        { text: 'Total Time', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'right' as const } }
      ]];

      chartData.topLocations.slice(0, 12).forEach((loc, idx) => {
        const hours = Math.floor(loc.duration / 60);
        const mins = loc.duration % 60;
        const timeStr = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
        const rowColor = idx % 2 === 0 ? 'f9fafb' : 'ffffff';
        // Show place name with coordinates below it
        const placeName = loc.placeName || '';
        const coords = loc.lat && loc.lng ? `${loc.lat.toFixed(4)}, ${loc.lng.toFixed(4)}` : loc.location;
        const displayLocation = placeName ? `${placeName}\n(${coords})` : coords;
        locTableData.push([
          { text: (idx + 1).toString(), options: { fill: { color: rowColor }, align: 'center' as const } },
          { text: displayLocation, options: { fill: { color: rowColor }, fontSize: 9 } },
          { text: loc.visits.toString(), options: { fill: { color: rowColor }, align: 'center' as const } },
          { text: timeStr, options: { fill: { color: rowColor }, align: 'right' as const } }
        ]);
      });

      locationsSlide.addTable(locTableData, { x: 0.5, y: 1.3, w: 12.3, rowH: 0.4, fontSize: 11, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [0.6, 8.0, 1.2, 2.5] });
    }

    // REPEATED VISITS SLIDE
    if (chartData.repeatedVisits && chartData.repeatedVisits.length > 0) {
      // Filter to only show locations visited 2+ times
      const repeatedLocations = chartData.repeatedVisits.filter(rv => rv.visitCount >= 2);
      
      if (repeatedLocations.length > 0) {
        currentSlide++;
        const repeatSlide = pptx.addSlide();
        if (contentBgDataUrl) addFullBleedBackground(repeatSlide, contentBgDataUrl);
        else repeatSlide.background = { color: colors.contentBg };
        addFooter(repeatSlide, 'PATHFINDER • UNCLASSIFIED', colors.lightText);
        addSlideNumber(repeatSlide, currentSlide, totalSlides, colors.lightText);
        if (logoDataUrl) addCornerLogo(repeatSlide, logoDataUrl);

        repeatSlide.addText('Repeated Visits', { x: 0.5, y: 0.4, w: 12.0, h: 0.5, fontSize: 28, bold: true, color: colors.primary });
        repeatSlide.addText(`Locations visited multiple times (${repeatedLocations.length} locations)`, { x: 0.5, y: 0.9, w: 12.0, h: 0.25, fontSize: 11, color: colors.lightText });

        const repeatTableData: any[] = [[
          { text: '#', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
          { text: 'Location', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
          { text: 'Visits', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
          { text: 'Total Time', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
          { text: 'First Visit', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
          { text: 'Last Visit', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } }
        ]];

        repeatedLocations.slice(0, 10).forEach((rv, idx) => {
          const hours = Math.floor(rv.totalDuration / 60);
          const mins = Math.round(rv.totalDuration % 60);
          const timeStr = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
          const rowColor = idx % 2 === 0 ? 'f9fafb' : 'ffffff';
          const firstDate = new Date(rv.firstVisit).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          const lastDate = new Date(rv.lastVisit).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          // Show location with coordinates
          const coords = `${rv.lat.toFixed(4)}, ${rv.lng.toFixed(4)}`;
          const isPlaceName = rv.location !== coords && !rv.location.includes(',');
          const displayLocation = isPlaceName ? `${rv.location}\n(${coords})` : coords;
          
          repeatTableData.push([
            { text: (idx + 1).toString(), options: { fill: { color: rowColor }, align: 'center' as const } },
            { text: displayLocation, options: { fill: { color: rowColor }, fontSize: 9 } },
            { text: rv.visitCount.toString(), options: { fill: { color: rowColor }, align: 'center' as const } },
            { text: timeStr, options: { fill: { color: rowColor }, align: 'center' as const } },
            { text: firstDate, options: { fill: { color: rowColor }, align: 'center' as const, fontSize: 9 } },
            { text: lastDate, options: { fill: { color: rowColor }, align: 'center' as const, fontSize: 9 } }
          ]);
        });

        repeatSlide.addTable(repeatTableData, { x: 0.5, y: 1.3, w: 12.3, rowH: 0.4, fontSize: 11, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [0.5, 5.5, 1.0, 1.5, 1.9, 1.9] });
        
        // Add insight text at bottom
        const totalRepeatedVisits = repeatedLocations.reduce((sum, rv) => sum + rv.visitCount, 0);
        repeatSlide.addText(`Pattern Analysis: ${repeatedLocations.length} locations were visited multiple times with a total of ${totalRepeatedVisits} visits. This indicates potential routine locations or areas of interest.`, 
          { x: 0.5, y: 6.2, w: 12.3, h: 0.6, fontSize: 10, color: colors.text, italic: true });
      }
    }

    // CO-LOCATIONS SLIDE
    if (chartData.coLocations && chartData.coLocations.length > 0) {
      currentSlide++;
      const colocSlide = pptx.addSlide();
      if (contentBgDataUrl) addFullBleedBackground(colocSlide, contentBgDataUrl);
      else colocSlide.background = { color: colors.contentBg };
      addFooter(colocSlide, 'PATHFINDER • UNCLASSIFIED', colors.lightText);
      addSlideNumber(colocSlide, currentSlide, totalSlides, colors.lightText);
      if (logoDataUrl) addCornerLogo(colocSlide, logoDataUrl);

      colocSlide.addText('Co-Location Analysis', { x: 0.5, y: 0.4, w: 12.0, h: 0.5, fontSize: 28, bold: true, color: colors.primary });
      colocSlide.addText(`Locations visited by multiple entities (${chartData.coLocations.length} shared locations)`, { x: 0.5, y: 0.9, w: 12.0, h: 0.25, fontSize: 11, color: colors.lightText });

      const colocTableData: any[] = [[
        { text: '#', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
        { text: 'Location', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
        { text: 'Entities', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
        { text: 'Overlaps', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'center' as const } },
        { text: 'Overlap Time', options: { bold: true, color: 'ffffff', fill: { color: colors.primary }, align: 'right' as const } }
      ]];

      chartData.coLocations.slice(0, 10).forEach((coloc, idx) => {
        const hours = Math.floor(coloc.totalOverlapTime / 60);
        const mins = Math.round(coloc.totalOverlapTime % 60);
        const timeStr = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
        const rowColor = idx % 2 === 0 ? 'f9fafb' : 'ffffff';
        const entitiesStr = coloc.entities.slice(0, 3).join(', ') + (coloc.entities.length > 3 ? ` +${coloc.entities.length - 3}` : '');
        // Show location with coordinates
        const coords = `${coloc.lat.toFixed(4)}, ${coloc.lng.toFixed(4)}`;
        const isPlaceName = coloc.location !== coords && !coloc.location.includes(',');
        const displayLocation = isPlaceName ? `${coloc.location}\n(${coords})` : coords;
        
        colocTableData.push([
          { text: (idx + 1).toString(), options: { fill: { color: rowColor }, align: 'center' as const } },
          { text: displayLocation, options: { fill: { color: rowColor }, fontSize: 9 } },
          { text: entitiesStr, options: { fill: { color: rowColor }, fontSize: 9 } },
          { text: coloc.overlapCount.toString(), options: { fill: { color: rowColor }, align: 'center' as const } },
          { text: timeStr, options: { fill: { color: rowColor }, align: 'right' as const } }
        ]);
      });

      colocSlide.addTable(colocTableData, { x: 0.5, y: 1.3, w: 12.3, rowH: 0.4, fontSize: 11, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [0.5, 4.5, 3.5, 1.3, 2.5] });
      
      // Add summary at bottom
      const totalEntities = new Set(chartData.coLocations.flatMap(c => c.entities)).size;
      colocSlide.addText(`Summary: ${totalEntities} entities shared ${chartData.coLocations.length} common locations. Co-location patterns may indicate shared routines, meetings, or areas of mutual interest.`,
        { x: 0.5, y: 6.2, w: 12.3, h: 0.6, fontSize: 10, color: colors.text, italic: true });
    }
  }

  // TIMELINE SLIDES
  if (includeTimeline && events.length > 0) {
    const timelineEvents = events.slice().sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
    const eventsPerSlide = TIMELINE_EVENTS_PER_SLIDE;
    const numberOfSlides = Math.ceil(timelineEvents.length / eventsPerSlide);

    for (let slideIndex = 0; slideIndex < numberOfSlides; slideIndex++) {
      currentSlide++;
      const slide = pptx.addSlide();
      if (contentBgDataUrl) addFullBleedBackground(slide, contentBgDataUrl);
      else slide.background = { color: colors.contentBg };
      addFooter(slide, 'PATHFINDER • UNCLASSIFIED', colors.lightText);
      addSlideNumber(slide, currentSlide, totalSlides, colors.lightText);
      if (logoDataUrl) addCornerLogo(slide, logoDataUrl);

      const startIndex = slideIndex * eventsPerSlide;
      const endIndex = Math.min(startIndex + eventsPerSlide, timelineEvents.length);
      const slideEvents = timelineEvents.slice(startIndex, endIndex);

      const dwellNumberById = new Map<string, number>();
      slideEvents.filter(e => e.type === 'dwell' && e.location).forEach((e, idx) => dwellNumberById.set(e.id, idx + 1));

      slide.addText(`Timeline Events (${startIndex + 1}–${endIndex} of ${events.length})`, { x: 0.5, y: 0.35, w: 12.0, h: 0.5, fontSize: 22, bold: true, color: colors.primary });

      const mapAreaX = 0.5, mapAreaY = 1.0, mapAreaW = 6.0, mapAreaH = 5.5;

      const timelineShot = (timelineMapScreenshots && timelineMapScreenshots[slideIndex]) || mapScreenshot;
      if (timelineShot) {
        const ar = timelineShot.aspectRatio;
        let mapImgW = mapAreaW, mapImgH = mapAreaW / ar;
        if (mapImgH > mapAreaH) { mapImgH = mapAreaH; mapImgW = mapAreaH * ar; }
        const mapImgX = mapAreaX + (mapAreaW - mapImgW) / 2;
        const mapImgY = mapAreaY + (mapAreaH - mapImgH) / 2;

        slide.addImage({ data: timelineShot.dataUrl, x: mapImgX, y: mapImgY, w: mapImgW, h: mapImgH });

        const proj = timelineShot.projectedPositions;
        if (proj) {
          for (const e of slideEvents.filter(ev => ev.type === 'dwell' && ev.location)) {
            const n = dwellNumberById.get(e.id);
            const p = proj[e.id];
            if (!n || !p) continue;
            const sx = mapImgX + (p.x / timelineShot.width) * mapImgW;
            const sy = mapImgY + (p.y / timelineShot.height) * mapImgH;
            const badgeSize = 0.2;
            slide.addShape('ellipse', { x: sx - badgeSize / 2, y: sy - badgeSize / 2, w: badgeSize, h: badgeSize, fill: { color: colors.accent }, line: { color: 'ffffff', width: 1 } });
            slide.addText(`${n}`, { x: sx - badgeSize / 2, y: sy - badgeSize / 2, w: badgeSize, h: badgeSize, fontSize: 7, bold: true, color: 'ffffff', align: 'center', valign: 'middle' });
          }
        }
      } else {
        slide.addText('Map not available', { x: mapAreaX, y: mapAreaY + 2.5, w: mapAreaW, h: 0.4, fontSize: 12, color: colors.secondary, italic: true, align: 'center' });
      }

      // Compact single-line format for each event with Dwell/Travel labels
      const bulletLines = slideEvents.map(ev => {
        const startStr = ev.startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        const endStr = ev.endTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        const duration = (ev.endTime.getTime() - ev.startTime.getTime()) / 1000;
        const durationStr = formatDuration(duration);

        if (ev.type === 'dwell') {
          const n = dwellNumberById.get(ev.id);
          const label = n ? `[${n}] Dwell: ` : '• Dwell: ';
          const loc = ev.address ? (ev.address.length > 35 ? ev.address.slice(0, 35) + '…' : ev.address) : (ev.location ? `${ev.location[0].toFixed(4)}, ${ev.location[1].toFixed(4)}` : '');
          const visits = ev.visitCount && ev.visitCount > 1 ? ` ×${ev.visitCount}` : '';
          return `${label}${startStr}–${endStr} (${durationStr}) ${loc}${visits}`;
        }
        const miles = (ev.distance || 0) * 0.621371;
        const dist = miles > 0 ? `${miles.toFixed(1)} mi` : `${ev.pointCount || 0} pts`;
        const trips = ev.tripCount && ev.tripCount > 1 ? ` ×${ev.tripCount}` : '';
        return `• Travel: ${startStr}–${endStr} (${durationStr}) ${dist}${trips}`;
      });

      slide.addText(bulletLines.join('\n'), { x: 6.8, y: 1.0, w: 6.0, h: 5.8, fontSize: 8, color: colors.text, align: 'left', valign: 'top', lineSpacing: 10, margin: 0.05 });
    }
  }

  // NOTES SLIDE
  if (eventNotes && Object.keys(eventNotes).length > 0) {
    currentSlide++;
    const notesSlide = pptx.addSlide();
    if (contentBgDataUrl) addFullBleedBackground(notesSlide, contentBgDataUrl);
    else notesSlide.background = { color: colors.contentBg };
    addFooter(notesSlide, 'PATHFINDER • UNCLASSIFIED', colors.lightText);
    addSlideNumber(notesSlide, currentSlide, totalSlides, colors.lightText);
    if (logoDataUrl) addCornerLogo(notesSlide, logoDataUrl);

    notesSlide.addText('Analyst Comments', { x: 0.5, y: 0.4, w: 12.0, h: 0.5, fontSize: 28, bold: true, color: colors.primary });

    const rows: any[] = [[
      { text: 'Event', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
      { text: 'Type', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
      { text: 'Time', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } },
      { text: 'Comment', options: { bold: true, color: 'ffffff', fill: { color: colors.primary } } }
    ]];

    const eventIndexById = new Map<string, number>();
    events.forEach((e, idx) => eventIndexById.set(e.id, idx + 1));

    const noteItems = Object.entries(eventNotes).flatMap(([eventId, notes]) => notes.map(n => ({ eventId, note: n }))).sort((a, b) => (eventIndexById.get(a.eventId) ?? 999999) - (eventIndexById.get(b.eventId) ?? 999999));

    noteItems.slice(0, 18).forEach(({ eventId, note }, idx) => {
      const ev = events.find(e => e.id === eventId);
      const num = eventIndexById.get(eventId);
      const rowColor = idx % 2 === 0 ? 'f9fafb' : 'ffffff';
      const timeStr = ev ? `${ev.startTime.toLocaleDateString()} ${ev.startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : '';
      rows.push([
        { text: num ? `#${num}` : eventId, options: { fill: { color: rowColor } } },
        { text: ev ? (ev.type === 'travel' ? 'Travel' : 'Dwell') : '', options: { fill: { color: rowColor } } },
        { text: timeStr, options: { fill: { color: rowColor }, fontSize: 9 } },
        { text: note.content, options: { fill: { color: rowColor }, fontSize: 10 } }
      ]);
    });

    notesSlide.addTable(rows, { x: 0.5, y: 1.1, w: 12.3, rowH: 0.35, fontSize: 10, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [0.8, 0.8, 2.0, 8.7] });
    if (noteItems.length > 18) {
      notesSlide.addText(`Showing 18 of ${noteItems.length} comments`, { x: 0.5, y: SLIDE_H - 0.6, w: 4.0, h: 0.25, fontSize: 9, color: colors.lightText, italic: true });
    }
  }

  const filename = `OSINT Explorer Story Timeline Report ${new Date().toISOString().split('T')[0]}.pptx`;
  await pptx.writeFile({ fileName: filename });
};

export interface MapScreenshotResult {
  dataUrl: string;
  width: number;
  height: number;
  aspectRatio: number;
  projectedPositions?: Record<string, { x: number; y: number }>;
}

/**
 * Wait for map to be ready (idle + tiles loaded)
 */
const waitForMapReady = async (map: any, timeoutMs: number = 2000): Promise<void> => {
  if (!map) return;
  
  return new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, timeoutMs);
    
    try {
      const isReady = map.isStyleLoaded?.() && map.areTilesLoaded?.() && !map.isMoving?.();
      if (isReady) {
        clearTimeout(timer);
        resolve();
        return;
      }
      
      map.once('idle', () => {
        clearTimeout(timer);
        resolve();
      });
    } catch {
      // If methods don't exist, just wait for timeout
    }
  });
};

/**
 * Capture the WebGL canvas synchronously during a render frame.
 * This is the most reliable way to capture Mapbox GL content.
 */
const captureWebGLCanvas = (map: any, canvas: HTMLCanvasElement, timeoutMs: number = 3000): Promise<string | null> => {
  return new Promise((resolve) => {
    // If no map reference, try direct capture
    if (!map) {
      try {
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(null);
      }
      return;
    }

    let resolved = false;
    const finish = (dataUrl: string | null) => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timer);
      try { map.off('render', onRender); } catch {}
      resolve(dataUrl);
    };

    const timer = setTimeout(() => {
      console.warn('[Map Capture] Render timeout, trying direct capture');
      try {
        finish(canvas.toDataURL('image/png'));
      } catch {
        finish(null);
      }
    }, timeoutMs);

    // Capture during the render callback - this is when the buffer is guaranteed to have content
    const onRender = () => {
      try {
        const dataUrl = canvas.toDataURL('image/png');
        if (dataUrl && dataUrl.length > 10000) {
          finish(dataUrl);
        }
      } catch (e) {
        console.warn('[Map Capture] Render capture failed:', e);
      }
    };

    try {
      map.on('render', onRender);
      // Force a repaint to trigger the render event
      map.triggerRepaint();
    } catch {
      // If events don't work, try direct capture
      try {
        finish(canvas.toDataURL('image/png'));
      } catch {
        finish(null);
      }
    }
  });
};

export const captureMapScreenshot = async (
  mapContainerId: string = 'pathfinder-map-container',
  projectPoints?: Array<{ id: string; lat: number; lon: number }>,
  includeOverlays: boolean = true
): Promise<MapScreenshotResult | undefined> => {
  try {
    const mapContainer = document.getElementById(mapContainerId);
    if (!mapContainer) {
      console.error('[Map Capture] Map container not found');
      return undefined;
    }

    // Get the Mapbox map instance - this is set in PathfinderMapGL.handleMapLoad
    const map: any = (window as any).__pathfinderMap;
    if (!map) {
      console.warn('[Map Capture] No map instance found on window.__pathfinderMap');
    }

    // Get the map canvas - MUST use map.getCanvas() for react-map-gl
    let mapCanvas: HTMLCanvasElement | null = null;
    if (map?.getCanvas) {
      try {
        mapCanvas = map.getCanvas();
      } catch (e) {
        console.warn('[Map Capture] Could not get canvas from map:', e);
      }
    }
    // Fallback to querySelector only if getCanvas failed
    if (!mapCanvas) {
      mapCanvas = mapContainer.querySelector('canvas.mapboxgl-canvas') as HTMLCanvasElement;
    }
    if (!mapCanvas) {
      console.error('[Map Capture] Map canvas not found');
      return undefined;
    }

    // Get dimensions from the canvas itself (more accurate for WebGL)
    const width = mapCanvas.clientWidth || mapCanvas.width;
    const height = mapCanvas.clientHeight || mapCanvas.height;
    const aspectRatio = width / height;

    // Ensure map is idle before capture
    if (map) {
      try {
        // Wait for any ongoing animations/movements to complete
        if (map.isMoving?.()) {
          await new Promise<void>(resolve => {
            map.once('moveend', resolve);
            setTimeout(resolve, 2000); // timeout fallback
          });
        }
        // Wait for tiles to load
        if (!map.areTilesLoaded?.()) {
          await new Promise<void>(resolve => {
            map.once('idle', resolve);
            setTimeout(resolve, 3000); // timeout fallback
          });
        }
      } catch (e) {
        console.warn('[Map Capture] Error waiting for map:', e);
      }
    }

    // Small delay to ensure GPU has flushed
    await new Promise(r => setTimeout(r, 200));

    // Capture the WebGL canvas during a render frame
    const mapDataUrl = await captureWebGLCanvas(map, mapCanvas, 3000);
    
    if (!mapDataUrl || mapDataUrl.length < 10000) {
      console.error('[Map Capture] WebGL capture failed or returned minimal data');
      // Try one more time with a longer wait
      await new Promise(r => setTimeout(r, 500));
      if (map?.triggerRepaint) map.triggerRepaint();
      await new Promise(r => setTimeout(r, 100));
    }

    let dataUrl: string;

    if (includeOverlays && mapDataUrl) {
      try {
        const html2canvas = (await import('html2canvas')).default;
        const scale = 2;
        const compositeCanvas = document.createElement('canvas');
        compositeCanvas.width = width * scale;
        compositeCanvas.height = height * scale;
        const ctx = compositeCanvas.getContext('2d');

        if (!ctx) {
          dataUrl = mapDataUrl;
        } else {
          // First draw the captured map
          const mapImg = new Image();
          await new Promise<void>((resolve, reject) => {
            mapImg.onload = () => resolve();
            mapImg.onerror = () => reject(new Error('Failed to load map image'));
            mapImg.src = mapDataUrl;
          });
          ctx.drawImage(mapImg, 0, 0, width * scale, height * scale);

          // Then capture HTML overlay elements (legend, controls, etc.)
          // We ignore the canvas and the container background
          const overlayCanvas = await html2canvas(mapContainer, {
            useCORS: true,
            allowTaint: true,
            backgroundColor: null,  // Transparent - we don't want container bg
            scale: scale,
            logging: false,
            width: width,
            height: height,
            ignoreElements: (element) => {
              // Ignore all canvas elements - we already drew the map
              if (element.tagName === 'CANVAS') return true;
              // Ignore the map container's direct div that has the background color
              if (element.classList?.contains('mapboxgl-map')) return false; // Keep this for controls
              return false;
            },
            onclone: (_doc, clonedEl) => {
              // Make the cloned container background transparent
              clonedEl.style.backgroundColor = 'transparent';
              // Also make any wrapper divs transparent
              const innerDiv = clonedEl.querySelector('.mapboxgl-map') as HTMLElement;
              if (innerDiv) innerDiv.style.backgroundColor = 'transparent';
            }
          });

          // Composite overlay on top of map
          ctx.drawImage(overlayCanvas, 0, 0);
          dataUrl = compositeCanvas.toDataURL('image/png');
        }
      } catch (error) {
        console.warn('[Map Capture] Overlay capture failed:', error);
        dataUrl = mapDataUrl;
      }
    } else if (mapDataUrl) {
      dataUrl = mapDataUrl;
    } else {
      // Last resort - try direct canvas capture
      console.warn('[Map Capture] All methods failed, trying direct toDataURL');
      try {
        dataUrl = mapCanvas.toDataURL('image/png');
      } catch (e) {
        console.error('[Map Capture] Direct capture also failed:', e);
        return undefined;
      }
    }

    // Project points to pixel coordinates
    let projectedPositions: Record<string, { x: number; y: number }> | undefined;
    if (projectPoints && projectPoints.length > 0 && map?.project) {
      projectedPositions = {};
      for (const p of projectPoints) {
        try {
          const pt = map.project([p.lon, p.lat]);
          if (pt && typeof pt.x === 'number' && typeof pt.y === 'number') {
            projectedPositions[p.id] = { x: pt.x, y: pt.y };
          }
        } catch {
          // Skip invalid points
        }
      }
    }

    return { dataUrl, width, height, aspectRatio, projectedPositions };
  } catch (error) {
    console.error('[Map Capture] Failed:', error);
    return undefined;
  }
};

/**
 * Temporarily zooms the live map to the provided points, captures a screenshot, and restores the previous view.
 * Intended for PowerPoint timeline "close-up" exports when dwells cluster tightly.
 */
export const captureMapScreenshotZoomedToPoints = async (opts: {
  mapContainerId?: string;
  fitPoints: Array<{ lat: number; lon: number }>;
  projectPoints?: Array<{ id: string; lat: number; lon: number }>;
  includeOverlays?: boolean;
  paddingPx?: number;
  maxZoom?: number;
  timeoutMs?: number;
}): Promise<MapScreenshotResult | undefined> => {
  const {
    mapContainerId = 'pathfinder-map-container',
    fitPoints,
    projectPoints,
    includeOverlays = true,
    paddingPx = 90,
    maxZoom = 16,
    timeoutMs = 1500,
  } = opts;

  const map: any = (window as any).__pathfinderMap;
  if (!map?.fitBounds || !map?.getCenter || !map?.jumpTo) {
    return captureMapScreenshot(mapContainerId, projectPoints, includeOverlays);
  }

  if (!fitPoints || fitPoints.length === 0) {
    return captureMapScreenshot(mapContainerId, projectPoints, includeOverlays);
  }

  let minLat = Infinity, maxLat = -Infinity, minLon = Infinity, maxLon = -Infinity;
  for (const p of fitPoints) {
    if (!Number.isFinite(p.lat) || !Number.isFinite(p.lon)) continue;
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lon < minLon) minLon = p.lon;
    if (p.lon > maxLon) maxLon = p.lon;
  }
  if (!Number.isFinite(minLat) || !Number.isFinite(minLon) || !Number.isFinite(maxLat) || !Number.isFinite(maxLon)) {
    return captureMapScreenshot(mapContainerId, projectPoints, includeOverlays);
  }

  const prevCenter = map.getCenter();
  const prev = {
    center: [prevCenter.lng, prevCenter.lat] as [number, number],
    zoom: map.getZoom?.(),
    bearing: map.getBearing?.(),
    pitch: map.getPitch?.(),
  };

  try {
    map.fitBounds([[minLon, minLat], [maxLon, maxLat]], { padding: paddingPx, duration: 0, maxZoom });
    await waitForMapReady(map, timeoutMs);
    return await captureMapScreenshot(mapContainerId, projectPoints, includeOverlays);
  } finally {
    try {
      map.jumpTo({ center: prev.center, zoom: prev.zoom, bearing: prev.bearing, pitch: prev.pitch });
      await waitForMapReady(map, timeoutMs);
    } catch {}
  }
};

// ==========================================
// LINK ANALYSIS POWERPOINT EXPORT
// ==========================================

export interface LinkAnalysisNode {
  id: string;
  type: 'device' | 'location' | 'phone' | 'url' | 'app' | 'website';
  label?: string;
  entity_id?: string;
  entity_type?: string;
  place_name?: string;
  latitude?: number;
  longitude?: number;
}

export interface LinkAnalysisEdge {
  source: string;
  target: string;
  type?: string;
  connection_type?: string;
  timestamp?: string;
  duration?: number;
  weight?: number;
}

export interface LinkAnalysisStats {
  totalNodes: number;
  totalLinks: number;
  deviceCount: number;
  locationCount: number;
  phoneCount: number;
  websiteCount: number;
  appCount: number;
  callCount: number;
  smsCount: number;
  browsingCount: number;
  appUsageCount: number;
  dwellCount: number;
  sharedLocationsCount?: number;
  dateRange?: { start: Date | null; end: Date | null };
  selectedEntities?: string[];
}

export interface LinkAnalysisExportOptions {
  title: string;
  subtitle?: string;
  nodes: LinkAnalysisNode[];
  links: LinkAnalysisEdge[];
  stats: LinkAnalysisStats;
  graphScreenshot?: { dataUrl: string; width: number; height: number };
  mapScreenshot?: { dataUrl: string; width: number; height: number };
  aiNarrative?: string;
  theme?: PowerPointTheme;
  includeGraph?: boolean;
  includeMap?: boolean;
  includeStats?: boolean;
}

/**
 * Capture the network graph canvas from react-force-graph-2d at higher resolution
 */
export const captureNetworkGraph = async (
  containerId: string = 'network-graph-container',
  scale: number = 2 // Default 2x resolution for better quality
): Promise<{ dataUrl: string; width: number; height: number } | undefined> => {
  try {
    const container = document.getElementById(containerId);
    if (!container) {
      console.error('[Graph Capture] Container not found:', containerId);
      return undefined;
    }

    // react-force-graph-2d renders to a canvas element inside the container
    const canvas = container.querySelector('canvas') as HTMLCanvasElement;
    if (!canvas) {
      console.error('[Graph Capture] Canvas not found in container');
      return undefined;
    }

    const width = canvas.clientWidth || canvas.width;
    const height = canvas.clientHeight || canvas.height;

    // Create a scaled canvas for higher resolution capture
    const scaledCanvas = document.createElement('canvas');
    scaledCanvas.width = width * scale;
    scaledCanvas.height = height * scale;
    const ctx = scaledCanvas.getContext('2d');

    if (!ctx) {
      console.error('[Graph Capture] Could not get 2D context');
      return undefined;
    }

    // Scale and draw the original canvas content
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.scale(scale, scale);
    ctx.drawImage(canvas, 0, 0, width, height);

    let dataUrl: string;
    try {
      dataUrl = scaledCanvas.toDataURL('image/png');
    } catch (e) {
      console.error('[Graph Capture] Failed to capture scaled canvas, trying original:', e);
      // Fallback to original canvas
      try {
        dataUrl = canvas.toDataURL('image/png');
        return { dataUrl, width: canvas.width, height: canvas.height };
      } catch (e2) {
        console.error('[Graph Capture] Failed to capture canvas:', e2);
        return undefined;
      }
    }

    return { dataUrl, width: scaledCanvas.width, height: scaledCanvas.height };
  } catch (error) {
    console.error('[Graph Capture] Failed:', error);
    return undefined;
  }
};

/**
 * Capture the Link Analysis map (NetworkGraphMap component)
 */
export const captureLinkAnalysisMap = async (
  containerId: string = 'network-map-container'
): Promise<{ dataUrl: string; width: number; height: number } | undefined> => {
  // Use the same capture logic as Pathfinder map
  const result = await captureMapScreenshot(containerId, undefined, true);
  if (result) {
    return {
      dataUrl: result.dataUrl,
      width: result.width,
      height: result.height
    };
  }
  return undefined;
};

/**
 * Generate AI narrative for Link Analysis data
 */
export const generateLinkAnalysisNarrative = async (
  nodes: LinkAnalysisNode[],
  links: LinkAnalysisEdge[],
  stats: LinkAnalysisStats,
  selectedEntities?: string[]
): Promise<string> => {
  try {
    // Build context in the format expected by the backend
    const networkStats = {
      totalNodes: stats.totalNodes,
      totalLinks: stats.totalLinks,
      deviceCount: stats.deviceCount,
      locationCount: stats.locationCount,
      phoneCount: stats.phoneCount,
      nodeTypes: {
        device: stats.deviceCount,
        location: stats.locationCount,
        phone: stats.phoneCount,
        website: stats.websiteCount,
        app: stats.appCount,
      },
      connectionTypes: {
        call: stats.callCount,
        sms: stats.smsCount,
        browsing: stats.browsingCount,
        app: stats.appUsageCount,
        dwell: stats.dwellCount,
      },
    };

    const deviceDetails = nodes
      .filter(n => n.type === 'device')
      .slice(0, 20)
      .map(n => ({
        id: n.id,
        label: n.label || n.id,
        entityType: n.entity_type || 'Device',
        connections: links.filter(l => l.source === n.id || l.target === n.id).length,
      }));

    const locationDetails = nodes
      .filter(n => n.type === 'location')
      .slice(0, 20)
      .map(n => ({
        id: n.id,
        name: n.place_name || n.label || n.id,
        type: 'Location',
        lat: n.latitude,
        lon: n.longitude,
      }));

    const apiContext = {
      networkStats,
      deviceDetails,
      locationDetails,
      selectedEntities: selectedEntities || [],
    };

    const response = await fetch('/api/link-analysis-chat/ask', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`,
      },
      body: JSON.stringify({
        question: 'Generate a comprehensive executive summary and analysis of this link analysis network data for a PowerPoint briefing. Include: 1) Key findings about the network structure, 2) Notable connection patterns between entities, 3) Significant locations or phone numbers, 4) Communication pattern insights, 5) Actionable recommendations for further investigation. Format with clear sections and bullet points for easy reading in a presentation.',
        context: apiContext,
      }),
    });

    if (response.ok) {
      const data = await response.json();
      return data.answer || '';
    }
    return '';
  } catch (error) {
    console.error('[Link Analysis] Failed to generate AI narrative:', error);
    return '';
  }
};

/**
 * Export Link Analysis data to PowerPoint
 */
export const exportLinkAnalysisToPowerPoint = async (options: LinkAnalysisExportOptions): Promise<void> => {
  const {
    title,
    subtitle,
    nodes: _nodes, // Reserved for future detailed analysis slides
    links: _links, // Reserved for future connection detail slides
    stats,
    graphScreenshot,
    mapScreenshot,
    aiNarrative,
    theme = 'standard',
    includeGraph = true,
    includeMap = true,
    includeStats = true,
  } = options;

  const PptxGenJS = await loadPptxGenJS();
  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_WIDE';
  pptx.author = 'Link Analysis System';
  pptx.company = 'Quiet Professionals';
  pptx.subject = 'Link Analysis Report';
  pptx.title = title;

  const colors = THEME_DEFINITIONS[theme].colors;
  const titleBgDataUrl = generateGradientBackground(1920, 1080, 'title', theme);
  const contentBgDataUrl = generateGradientBackground(1920, 1080, 'content', theme);

  let logoDataUrl = '';
  try {
    logoDataUrl = await fetchAsDataUrl('/Circle_Endurion_White.png');
  } catch (e) {
    console.warn('Failed to load logo:', e);
  }

  // TITLE SLIDE
  const titleSlide = pptx.addSlide();
  if (titleBgDataUrl) addFullBleedBackground(titleSlide, titleBgDataUrl);
  else titleSlide.background = { color: colors.titleBg };
  if (logoDataUrl) addCornerLogo(titleSlide, logoDataUrl, { x: SLIDE_W - 1.5, y: SLIDE_H - 1.5, w: 1.1, h: 1.1 });

  titleSlide.addText(title, { x: 0.75, y: 2.2, w: SLIDE_W - 1.5, h: 1.0, fontSize: 40, bold: true, color: colors.titleText, align: 'left' });
  if (subtitle) {
    titleSlide.addText(subtitle, { x: 0.75, y: 3.3, w: SLIDE_W - 1.5, h: 0.5, fontSize: 22, color: colors.titleText, align: 'left' });
  }
  
  const entityList = stats.selectedEntities?.join(', ') || 'All Entities';
  titleSlide.addText(`ENTITIES: ${entityList.toUpperCase()}`, { x: 0.75, y: 4.1, w: SLIDE_W - 1.5, h: 0.4, fontSize: 14, color: colors.accent, align: 'left', bold: true });
  
  if (stats.dateRange?.start && stats.dateRange?.end) {
    const dateStr = `${stats.dateRange.start.toLocaleDateString()} – ${stats.dateRange.end.toLocaleDateString()}`;
    titleSlide.addText(`Analysis Period: ${dateStr}`, { x: 0.75, y: 4.6, w: SLIDE_W - 1.5, h: 0.3, fontSize: 12, color: colors.titleText, align: 'left' });
  }
  
  titleSlide.addText(`Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`, { x: 0.75, y: 5.5, w: SLIDE_W - 1.5, h: 0.3, fontSize: 12, color: colors.titleText, align: 'left' });

  // STATISTICS SLIDE
  if (includeStats) {
    const statsSlide = pptx.addSlide();
    if (contentBgDataUrl) addFullBleedBackground(statsSlide, contentBgDataUrl);
    else statsSlide.background = { color: colors.contentBg };
    addFooter(statsSlide, 'LINK ANALYSIS • UNCLASSIFIED', colors.lightText);
    if (logoDataUrl) addCornerLogo(statsSlide, logoDataUrl);

    statsSlide.addText('Network Statistics', { x: 0.5, y: 0.4, w: 12.0, h: 0.6, fontSize: 28, bold: true, color: colors.primary });

    // Network overview stats
    const networkStats = [
      ['Total Nodes', stats.totalNodes.toLocaleString()],
      ['Total Connections', stats.totalLinks.toLocaleString()],
      ['Devices', stats.deviceCount.toLocaleString()],
      ['Locations', stats.locationCount.toLocaleString()],
      ['Phone Numbers', stats.phoneCount.toLocaleString()],
      ['Websites', stats.websiteCount.toLocaleString()],
      ['Apps', stats.appCount.toLocaleString()],
    ].map(([label, value], idx) => [
      { text: label, options: { bold: true, color: colors.text, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } },
      { text: value, options: { color: colors.secondary, align: 'right' as const, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } }
    ]);

    statsSlide.addText('NETWORK OVERVIEW', { x: 0.5, y: 1.1, w: 5.5, h: 0.3, fontSize: 12, bold: true, color: colors.primary });
    statsSlide.addTable(networkStats, { x: 0.5, y: 1.45, w: 5.5, rowH: 0.4, fontSize: 12, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [3.5, 2.0] });

    // Connection type stats
    const connectionStats = [
      ['Call Logs', stats.callCount.toLocaleString()],
      ['SMS Messages', stats.smsCount.toLocaleString()],
      ['Browsing History', stats.browsingCount.toLocaleString()],
      ['App Usage', stats.appUsageCount.toLocaleString()],
      ['Dwell Locations', stats.dwellCount.toLocaleString()],
    ].map(([label, value], idx) => [
      { text: label, options: { bold: true, color: colors.text, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } },
      { text: value, options: { color: colors.secondary, align: 'right' as const, fill: { color: idx % 2 === 0 ? 'f3f4f6' : 'ffffff' } } }
    ]);

    statsSlide.addText('CONNECTION TYPES', { x: 6.8, y: 1.1, w: 5.5, h: 0.3, fontSize: 12, bold: true, color: colors.primary });
    statsSlide.addTable(connectionStats, { x: 6.8, y: 1.45, w: 5.5, rowH: 0.4, fontSize: 12, border: { type: 'solid', pt: 1, color: 'e5e7eb' }, colW: [3.5, 2.0] });

    if (stats.sharedLocationsCount && stats.sharedLocationsCount > 0) {
      statsSlide.addText(`Shared Locations: ${stats.sharedLocationsCount} locations visited by multiple entities`, { x: 0.5, y: 5.5, w: 12.0, h: 0.4, fontSize: 14, color: colors.accent, bold: true });
    }
  }

  // COMBINED NETWORK GRAPH + MAP SLIDE
  if ((includeGraph && graphScreenshot) || (includeMap && mapScreenshot)) {
    const vizSlide = pptx.addSlide();
    if (contentBgDataUrl) addFullBleedBackground(vizSlide, contentBgDataUrl);
    else vizSlide.background = { color: colors.contentBg };
    addFooter(vizSlide, 'LINK ANALYSIS • UNCLASSIFIED', colors.lightText);
    if (logoDataUrl) addCornerLogo(vizSlide, logoDataUrl);

    vizSlide.addText('Network Analysis', { x: 0.5, y: 0.4, w: 12.0, h: 0.5, fontSize: 28, bold: true, color: colors.primary });

    // Build context summary paragraph from stats
    const contextParts: string[] = [];
    const entityNames = stats.selectedEntities?.join(', ') || 'selected entities';
    
    // Core network summary
    contextParts.push(`This analysis visualizes the digital footprint of ${entityNames}, comprising ${stats.totalNodes.toLocaleString()} nodes and ${stats.totalLinks.toLocaleString()} connections.`);
    
    // Node breakdown
    const nodeTypes: string[] = [];
    if (stats.deviceCount > 0) nodeTypes.push(`${stats.deviceCount} device${stats.deviceCount !== 1 ? 's' : ''}`);
    if (stats.locationCount > 0) nodeTypes.push(`${stats.locationCount} location${stats.locationCount !== 1 ? 's' : ''}`);
    if (stats.phoneCount > 0) nodeTypes.push(`${stats.phoneCount} phone number${stats.phoneCount !== 1 ? 's' : ''}`);
    if (stats.websiteCount > 0) nodeTypes.push(`${stats.websiteCount} website${stats.websiteCount !== 1 ? 's' : ''}`);
    if (stats.appCount > 0) nodeTypes.push(`${stats.appCount} app${stats.appCount !== 1 ? 's' : ''}`);
    if (nodeTypes.length > 0) {
      contextParts.push(`The network includes ${nodeTypes.join(', ')}.`);
    }
    
    // Connection types
    const connTypes: string[] = [];
    if (stats.callCount > 0) connTypes.push(`${stats.callCount} call${stats.callCount !== 1 ? 's' : ''}`);
    if (stats.smsCount > 0) connTypes.push(`${stats.smsCount} SMS message${stats.smsCount !== 1 ? 's' : ''}`);
    if (stats.dwellCount > 0) connTypes.push(`${stats.dwellCount} dwell event${stats.dwellCount !== 1 ? 's' : ''}`);
    if (stats.browsingCount > 0) connTypes.push(`${stats.browsingCount} web visit${stats.browsingCount !== 1 ? 's' : ''}`);
    if (connTypes.length > 0) {
      contextParts.push(`Activity captured: ${connTypes.join(', ')}.`);
    }
    
    // Shared locations insight
    if (stats.sharedLocationsCount && stats.sharedLocationsCount > 0) {
      contextParts.push(`${stats.sharedLocationsCount} shared location${stats.sharedLocationsCount !== 1 ? 's were' : ' was'} identified where multiple entities co-located.`);
    }
    
    const contextSummary = contextParts.join(' ');

    // If we have both, show side by side with context at bottom
    if (includeGraph && graphScreenshot && includeMap && mapScreenshot) {
      // Left side: Network Graph
      const leftX = 0.4, leftY = 0.95;
      const halfW = 6.2, halfH = 5.0;
      
      vizSlide.addText('Network Graph', { x: leftX, y: leftY, w: halfW, h: 0.25, fontSize: 10, bold: true, color: colors.primary });
      
      const graphAr = graphScreenshot.width / graphScreenshot.height;
      let graphW = halfW, graphH = halfW / graphAr;
      if (graphH > halfH - 0.3) { graphH = halfH - 0.3; graphW = graphH * graphAr; }
      const graphX = leftX + (halfW - graphW) / 2;
      const graphY = leftY + 0.28 + (halfH - 0.3 - graphH) / 2;
      
      vizSlide.addImage({ data: graphScreenshot.dataUrl, x: graphX, y: graphY, w: graphW, h: graphH });

      // Right side: Map
      const rightX = 6.7, rightY = 0.95;
      
      vizSlide.addText('Location Analysis', { x: rightX, y: rightY, w: halfW, h: 0.25, fontSize: 10, bold: true, color: colors.primary });
      
      const mapAr = mapScreenshot.width / mapScreenshot.height;
      let mapW = halfW, mapH = halfW / mapAr;
      if (mapH > halfH - 0.3) { mapH = halfH - 0.3; mapW = mapH * mapAr; }
      const mapX = rightX + (halfW - mapW) / 2;
      const mapY = rightY + 0.28 + (halfH - 0.3 - mapH) / 2;
      
      vizSlide.addImage({ data: mapScreenshot.dataUrl, x: mapX, y: mapY, w: mapW, h: mapH });
      
      // Context summary at bottom
      vizSlide.addText(contextSummary, { 
        x: 0.4, y: 6.1, w: 12.5, h: 0.9, 
        fontSize: 9, color: colors.text, 
        align: 'left', valign: 'top', 
        lineSpacing: 11,
        fill: { color: 'f8f9fa' },
        margin: 0.1
      });
    } else if (includeGraph && graphScreenshot) {
      // Only graph - show full width with context at bottom
      const maxW = 12.0, maxH = 4.8;
      const ar = graphScreenshot.width / graphScreenshot.height;
      let imgW = maxW, imgH = maxW / ar;
      if (imgH > maxH) { imgH = maxH; imgW = maxH * ar; }
      const imgX = 0.5 + (maxW - imgW) / 2;
      const imgY = 0.95 + (maxH - imgH) / 2;

      vizSlide.addImage({ data: graphScreenshot.dataUrl, x: imgX, y: imgY, w: imgW, h: imgH });
      
      // Context summary at bottom
      vizSlide.addText(contextSummary, { 
        x: 0.4, y: 5.9, w: 12.5, h: 1.1, 
        fontSize: 10, color: colors.text, 
        align: 'left', valign: 'top', 
        lineSpacing: 12,
        fill: { color: 'f8f9fa' },
        margin: 0.12
      });
    } else if (includeMap && mapScreenshot) {
      // Only map - show full width with context at bottom
      const maxW = 12.0, maxH = 4.8;
      const ar = mapScreenshot.width / mapScreenshot.height;
      let imgW = maxW, imgH = maxW / ar;
      if (imgH > maxH) { imgH = maxH; imgW = maxH * ar; }
      const imgX = 0.5 + (maxW - imgW) / 2;
      const imgY = 0.95 + (maxH - imgH) / 2;

      vizSlide.addImage({ data: mapScreenshot.dataUrl, x: imgX, y: imgY, w: imgW, h: imgH });
      
      // Context summary at bottom
      vizSlide.addText(contextSummary, { 
        x: 0.4, y: 5.9, w: 12.5, h: 1.1, 
        fontSize: 10, color: colors.text, 
        align: 'left', valign: 'top', 
        lineSpacing: 12,
        fill: { color: 'f8f9fa' },
        margin: 0.12
      });
    }
  }

  // AI ANALYSIS SLIDE
  if (aiNarrative) {
    const cleanNarrative = sanitizeAiNarrative(aiNarrative);
    const chunks = splitTextForSlides(cleanNarrative, 2200);

    for (let i = 0; i < chunks.length; i++) {
      const aiSlide = pptx.addSlide();
      if (contentBgDataUrl) addFullBleedBackground(aiSlide, contentBgDataUrl);
      else aiSlide.background = { color: colors.contentBg };
      addFooter(aiSlide, 'LINK ANALYSIS • UNCLASSIFIED', colors.lightText);
      if (logoDataUrl) addCornerLogo(aiSlide, logoDataUrl);

      const slideTitle = i === 0 ? 'AI-Generated Analysis' : 'AI-Generated Analysis (continued)';
      aiSlide.addText(slideTitle, { x: 0.5, y: 0.4, w: 12.0, h: 0.6, fontSize: 28, bold: true, color: colors.primary });

      if (i === 0) {
        // First slide has analyst comments box
        aiSlide.addText('ANALYST COMMENTS', { x: 0.5, y: 1.1, w: 12.3, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
        aiSlide.addText('[Enter analyst observations and assessments here]', { x: 0.5, y: 1.45, w: 12.3, h: 0.7, fontSize: 11, italic: true, color: '888888', line: { color: 'cccccc', width: 1 }, margin: 0.1, valign: 'top' });

        aiSlide.addText(chunks[i], { x: 0.5, y: 2.4, w: 12.3, h: 4.5, fontSize: 10, color: colors.text, align: 'left', valign: 'top', lineSpacing: 12, margin: 0.1 });
      } else {
        aiSlide.addText(chunks[i], { x: 0.5, y: 1.1, w: 12.3, h: 5.8, fontSize: 10, color: colors.text, align: 'left', valign: 'top', lineSpacing: 12, margin: 0.1 });
      }
    }
  }

  // OBSERVATIONS SLIDE
  const obsSlide = pptx.addSlide();
  if (contentBgDataUrl) addFullBleedBackground(obsSlide, contentBgDataUrl);
  else obsSlide.background = { color: colors.contentBg };
  addFooter(obsSlide, 'LINK ANALYSIS • UNCLASSIFIED', colors.lightText);
  if (logoDataUrl) addCornerLogo(obsSlide, logoDataUrl);

  obsSlide.addText('Observations and Recommendations', { x: 0.5, y: 0.4, w: 12.0, h: 0.6, fontSize: 28, bold: true, color: colors.primary });
  
  // Cadre/Analyst comments block at top (full width)
  obsSlide.addText('CADRE COMMENTS', { x: 0.5, y: 1.1, w: 12.3, h: 0.3, fontSize: 11, bold: true, color: colors.primary });
  obsSlide.addText('[Enter cadre observations and assessments here]', { x: 0.5, y: 1.45, w: 12.3, h: 0.8, fontSize: 11, italic: true, color: '888888', line: { color: 'cccccc', width: 1 }, margin: 0.1, valign: 'top' });

  // Extract AI-generated bullets from narrative
  const bullets = aiNarrative ? narrativeToBullets(aiNarrative, 10) : [];
  const keyFindings = bullets.slice(0, 5);
  const actionable = bullets.slice(5, 10);

  obsSlide.addText('AI GENERATED KEY FINDINGS', { x: 0.5, y: 2.5, w: 5.9, h: 0.35, fontSize: 12, bold: true, color: colors.primary });
  obsSlide.addText(
    (keyFindings.length ? keyFindings : ['[Add key findings from analysis]']).join('\n'), 
    { x: 0.5, y: 2.9, w: 5.9, h: 4.0, fontSize: 10, color: keyFindings.length ? colors.text : '888888', valign: 'top', bullet: keyFindings.length ? { indent: 14 } : undefined, lineSpacing: 13, margin: 0.08, italic: !keyFindings.length }
  );

  obsSlide.addText('AI GENERATED ACTIONABLE CONCLUSIONS', { x: 6.8, y: 2.5, w: 5.9, h: 0.35, fontSize: 12, bold: true, color: colors.primary });
  obsSlide.addText(
    (actionable.length ? actionable : ['[Add recommended actions]']).join('\n'), 
    { x: 6.8, y: 2.9, w: 5.9, h: 4.0, fontSize: 10, color: actionable.length ? colors.text : '888888', valign: 'top', bullet: actionable.length ? { indent: 14 } : undefined, lineSpacing: 13, margin: 0.08, italic: !actionable.length }
  );

  // Generate filename
  const entityStr = stats.selectedEntities?.slice(0, 3).join('-') || 'network';
  const filename = `link-analysis-${entityStr}-${new Date().toISOString().split('T')[0]}.pptx`;
  await pptx.writeFile({ fileName: filename });
};
